import { Controller } from "stimulus";
import { stateParams, fetchParams, applyFilter, MOBILE_QUERY } from "../../training_grooming_calendar/url_state";
import { nowOffset } from "../../training_grooming_calendar/now_line";
import { closeOverlay } from "../../training_grooming_calendar/overlay";
import { captureScroll, restoreScroll } from "../../training_grooming_calendar/scroll_state";
import { FILTER_RELOAD } from "../../training_grooming_calendar/filter_sections";

export default class extends Controller {
  static values = {
    url: String,
    date: String,
    view: String,
    tab: String,
    facility: String,
    categories: Array,
    lanes: String,
    service: String,
    blocks: String,
    schedules: String,
    scheduled: String,
  };

  static targets = ["content", "scroller", "nowLine"];

  connect() {
    this.loadToken = 0;
    this.collapsed = new Set();
    this.limits = {};
    this.media = window.matchMedia(MOBILE_QUERY);
    this.handleMedia = () => this.load();
    this.media.addEventListener("change", this.handleMedia);
    this.load();
  }

  disconnect() {
    this.media.removeEventListener("change", this.handleMedia);
    this.stopClock();
  }

  goTo(event) {
    this.dateValue = event.currentTarget.dataset.date;
    this.load();
  }

  setView(event) {
    this.viewValue = event.currentTarget.dataset.view;
    this.load();
  }

  setTab(event) {
    this.tabValue = event.currentTarget.dataset.tab;
    this.load();
  }

  openDay(event) {
    this.viewValue = "day";
    this.dateValue = event.currentTarget.dataset.date;
    this.load();
  }

  filter(event) {
    const { state, limits } = applyFilter(this.state(), this.limits, event.detail);
    this.limits = limits;
    this.facilityValue = state.facility;
    this.categoriesValue = state.categories;
    this.lanesValue = state.lanes;
    this.serviceValue = state.service;
    this.blocksValue = state.blocks;
    this.schedulesValue = state.schedules;
    this.scheduledValue = state.scheduled;
    this.load({ reason: FILTER_RELOAD });
  }

  showAllFacilities() {
    this.facilityValue = "all";
    this.limits = {};
    this.load();
  }

  loadMore(event) {
    const { kind, limit } = event.currentTarget.dataset;
    this.limits[kind] = Number(limit);
    this.load();
  }

  reload(event) {
    this.load({ keepScroll: true, reason: event && event.detail ? event.detail.reason : undefined });
  }

  pickDate(event) {
    const instance = flatpickr(event.currentTarget, {
      disableMobile: "true",
      defaultDate: this.dateValue,
      onChange: (selectedDates, dateStr) => {
        if (selectedDates.length !== 1) return;
        instance.destroy();
        this.dateValue = dateStr;
        this.load();
      },
    });
    instance.open();
  }

  toggleGroup(event) {
    const kind = event.currentTarget.closest(".tg-group").dataset.kind;
    this.collapsed.has(kind) ? this.collapsed.delete(kind) : this.collapsed.add(kind);
    this.applyCollapsed();
  }

  load({ keepScroll = false, reason = "other" } = {}) {
    const token = ++this.loadToken;
    const scroll = keepScroll ? captureScroll(this.contentTarget, window) : null;
    delete this.contentTarget.dataset.tgRestoring;
    const params = fetchParams(this.state(), { mobile: this.media.matches, limits: this.limits });
    this.contentTarget.classList.add("tg-content-loading");
    fetch(`${this.urlValue}?${params.toString()}`, { headers: { "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        if (token !== this.loadToken) return;
        closeOverlay({ restoreFocus: false });
        this.contentTarget.dataset.tgLoadReason = reason;
        this.contentTarget.innerHTML = html;
        this.contentTarget.classList.remove("tg-content-loading");
        this.afterRender(scroll);
      })
      .catch(() => {
        if (token !== this.loadToken) return;
        this.contentTarget.classList.remove("tg-content-loading");
        this.contentTarget.innerHTML = '<div class="tg-error">The calendar could not be loaded. Please refresh the page and try again.</div>';
      });
  }

  afterRender(scroll) {
    const url = `${window.location.pathname}?${stateParams(this.state()).toString()}`;
    window.history.replaceState(window.history.state, "", url);
    this.applyCollapsed();
    if (scroll) {
      this.contentTarget.dataset.tgRestoring = "true";
      restoreScroll(this.contentTarget, window, scroll);
    } else {
      this.scrollToOffset();
    }
    this.startClock();
    this.element.dispatchEvent(new CustomEvent("training-grooming:rendered"));
  }

  state() {
    return {
      date: this.dateValue,
      view: this.viewValue,
      tab: this.tabValue,
      facility: this.facilityValue,
      categories: this.categoriesValue,
      lanes: this.lanesValue,
      service: this.serviceValue,
      blocks: this.blocksValue,
      schedules: this.schedulesValue,
      scheduled: this.scheduledValue,
    };
  }

  applyCollapsed() {
    this.contentTarget.querySelectorAll(".tg-group").forEach((group) => {
      const collapsed = this.collapsed.has(group.dataset.kind);
      group.classList.toggle("tg-group-collapsed", collapsed);
      const button = group.querySelector("[data-action~='training-grooming-calendar--calendar#toggleGroup']");
      if (button) button.setAttribute("aria-expanded", collapsed ? "false" : "true");
    });
  }

  scrollToOffset() {
    if (!this.hasScrollerTarget) return;
    this.scrollerTarget.scrollTop = Number(this.scrollerTarget.dataset.scrollOffset || 0);
  }

  startClock() {
    this.stopClock();
    if (!this.hasNowLineTarget) return;
    const line = this.nowLineTarget;
    const renderedOffset = Number(line.dataset.offset);
    const renderedAt = Date.now();
    const windowLength = Number(getComputedStyle(line).getPropertyValue("--tg-window")) || Infinity;
    this.clock = setInterval(() => {
      const offset = nowOffset({ renderedOffset, renderedAt, now: Date.now(), windowLength });
      line.hidden = offset === null;
      if (offset !== null) line.style.setProperty("--tg-now", offset);
    }, 30 * 1000);
  }

  stopClock() {
    if (this.clock) clearInterval(this.clock);
    this.clock = null;
  }
}
